import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import Interaction from "../../../../types/interaction/Interaction";
import { Close, Edit } from "@mui/icons-material";
import { useContext, useState } from "react";
import { useBulkUpdate } from "../../../../hooks/useInteraction";
import LoadingOverlay from "../../../../components/common/LoadingIndicator/LoadingOverlay";
import {
  BulkUpdateInteractionsRequest,
  BulkUpdateInteractionsResponse,
  BulkUpdateItem,
  BulkUpdateItemProperties,
  CollectionUpdate,
} from "../../../../types/interaction/BulkUpdate";
import { useAlert } from "../../../../context/AlertContext";
import { AuthContext } from "../../../../context/AuthContext";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import CustomTabPanel from "../../../../components/common/CustomPanel/CustomTabPanel";
import DisplayBulkEditResponse from "./DisplayBulkUpdateResponse";
import {
  BulkUpdateRetentionTab,
  BulkUpdateTagsTab,
  BulkUpdateViewsTab,
} from "./BulkUpdateTabs";
import { useTranslation } from "react-i18next";
import { useFeatureFlagToken } from "../../../../hooks/useFeatureFlag";
import { FeatureFlagName } from "../../../../types/feature-flags/featureFlag";

const FORM_ID = "bulk_update_interactions";

const DEFAULT_VALUES: BulkUpdateFormValues = {
  retentionDate: null,
  removeRetention: false,
  putOnHold: false,
  tagsToAdd: [],
  tagsToRemove: [],
  viewsToAdd: [],
  viewsToRemove: [],
};

export type BulkUpdateFormValues = {
  retentionDate: number | null;
  removeRetention: boolean;
  putOnHold: boolean;
  tagsToAdd: string[];
  tagsToRemove: string[];
  viewsToAdd: string[];
  viewsToRemove: string[];
};

const schema = z.object({
  retentionDate: z.union([
    z.number(), // Valid future timestamp
    z.null(), // Allows null values
  ]),
  removeRetention: z.boolean(),
  putOnHold: z.boolean(),
  tagsToAdd: z.array(z.string()).optional(),
  tagsToRemove: z.array(z.string()).optional(),
  viewsToAdd: z.array(z.string()).optional(),
  viewsToRemove: z.array(z.string()).optional(),
});

/** Taken from MUI */
function a11yProps(index: number) {
  return {
    id: `tab-${index}`,
    "aria-controls": `tabpanel-${index}`,
  };
}

interface BulkUpdateProps {
  interactions: Interaction[];
  customerId: string | undefined;
}

const buildBulkUpdateRequest = (
  formData: BulkUpdateFormValues,
  interactionId: string
) => {
  //dynamically built the request to follow PATCH
  const updateTags =
    formData.tagsToAdd.length > 0 || formData.tagsToRemove.length > 0;
  const updateViews =
    formData.viewsToAdd.length > 0 || formData.viewsToRemove.length > 0;

  const tagsUpdateItem: CollectionUpdate = {
    ...(formData.tagsToAdd.length > 0 && { add: formData.tagsToAdd }),
    ...(formData.tagsToRemove.length > 0 && {
      remove: formData.tagsToRemove,
    }),
  };

  const viewsUpdateItem: CollectionUpdate = {
    ...(formData.viewsToAdd.length > 0 && { add: formData.viewsToAdd }),
    ...(formData.viewsToRemove.length > 0 && {
      remove: formData.viewsToRemove,
    }),
  };

  const fields: BulkUpdateItemProperties = {
    ...((formData.retentionDate || formData.removeRetention) && {
      retentionDate: formData.removeRetention ? 0 : formData.retentionDate!,
    }),
    ...(formData.putOnHold && { legalHold: true }),
    ...(updateTags && { tags: tagsUpdateItem }),
    ...(updateViews && { views: viewsUpdateItem }),
  };

  const bulkUpdateItem: BulkUpdateItem = {
    interactionId: interactionId,
    updates: fields,
  };

  return bulkUpdateItem;
};
/**
 * Displays a modal, allowing a user to bulk edit interactions
 * After submitted the modal stays open and displays results
 * @param interactions all selected interaction objects to edit
 * @param customerId the current customer
 * @constructor
 */
export default function BulkUpdateModal({
  interactions,
  customerId,
}: Readonly<BulkUpdateProps>) {
  const { t } = useTranslation("common");
  const [isOpen, setIsOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const isViewsEnabled = useFeatureFlagToken(FeatureFlagName.Views);

  // to track most recent calls and recall failures if needed
  const [apiResponse, setApiResponse] = useState<
    BulkUpdateInteractionsResponse | undefined
  >(undefined);
  const [apiRequest, setApiRequest] =
    useState<BulkUpdateInteractionsRequest | null>(null);

  const showAlert = useAlert();

  const bulkUpdateMutation = useBulkUpdate();
  const { isPending } = bulkUpdateMutation;

  const { user } = useContext(AuthContext);

  const canEdit = user?.hasPermission("interactions:Update");

  const handleOpen = () => {
    setIsOpen(true);
    setApiResponse(undefined);
    setApiRequest(null);
  };

  const handleClose = () => {
    reset();
    setIsOpen(false);
    setApiResponse(undefined);
    setApiRequest(null);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const form = useForm<BulkUpdateFormValues>({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: {
      ...DEFAULT_VALUES,
    },
  });

  const { control, formState, reset, handleSubmit, watch, setValue } = form;
  const { errors, isDirty } = formState;

  // track all views and tags to add and remove
  const currentTagsToAdd = watch("tagsToAdd");
  const currentTagsToRemove = watch("tagsToRemove");
  const currentViewsToAdd = watch("viewsToAdd");
  const currentViewsToRemove = watch("viewsToRemove");
  const clearRetentionDate = watch("removeRetention");

  // Stores the request and response in state for rerunning and displaying info
  const tryUpdate = async (request: BulkUpdateInteractionsRequest) => {
    //store the last request in state
    setApiRequest(request);
    try {
      const response = await bulkUpdateMutation.mutateAsync(request);
      setApiResponse(response);
    } catch (error: unknown) {
      if (error instanceof Error) {
        showAlert(error.message, "error");
      } else {
        showAlert("An unexpected error occurred when bulk updating.");
      }
      setApiResponse(undefined);
    }
  };

  //submits request from form
  const submitRequest = async (formData: BulkUpdateFormValues) => {
    const request: BulkUpdateInteractionsRequest = {
      customerId: customerId ?? "",
      changes: [],
    };

    interactions.forEach((interaction) => {
      request.changes.push(
        buildBulkUpdateRequest(formData, interaction.interactionId)
      );
    });
    tryUpdate(request);
  };

  //function that will rerun failed jobs based on last request and response
  const rerunFailed = async () => {
    if (!apiRequest || !apiResponse) return;

    // Get the list of failed interaction IDs
    const failedResultIds = apiResponse.results
      .filter(
        (result) => result.status !== "OK" && result.status !== "BadRequest"
      )
      .map((result) => result.interactionId);

    // Find update items from the original request that failed
    const failedUpdateItems = apiRequest.changes.filter((change) =>
      failedResultIds.includes(change.interactionId)
    );

    const newRequest: BulkUpdateInteractionsRequest = {
      customerId: apiRequest.customerId,
      changes: failedUpdateItems,
    };

    tryUpdate(newRequest);
  };

  //interactions in legal hold cannot be bulk edited
  const disableBulk = interactions.some(
    (interaction) => interaction.legalHold === true
  );

  const noSelectedInteractions = interactions.length <= 0;

  return (
    <>
      <div>
        <Tooltip
          title={
            disableBulk
              ? "Cannot bulk edit interactions on legal hold."
              : "Bulk edit selected interactions."
          }
          placement="bottom"
          arrow
        >
          <span>
            <Button
              variant="outlined"
              onClick={handleOpen}
              disabled={disableBulk || !canEdit || noSelectedInteractions}
              startIcon={<Edit />}
            >
              {t("common:bulkEdit")}
            </Button>
          </span>
        </Tooltip>
      </div>
      <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>
          <Typography variant="h6">Bulk Edit Interactions</Typography>

          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {apiResponse ? (
            <DisplayBulkEditResponse
              response={apiResponse}
              rerun={rerunFailed}
            />
          ) : (
            <form id={FORM_ID} onSubmit={handleSubmit(submitRequest)}>
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs value={tabValue} onChange={handleTabChange}>
                  <Tab label="Retention Date" {...a11yProps(0)} />
                  <Tab label="Tags" {...a11yProps(1)} />
                  {isViewsEnabled && <Tab label="Views" {...a11yProps(2)} />}
                </Tabs>
              </Box>
              <CustomTabPanel value={tabValue} index={0}>
                <BulkUpdateRetentionTab
                  control={control}
                  errors={errors}
                  clearRetentionDate={clearRetentionDate}
                  setValue={setValue}
                />
              </CustomTabPanel>
              <CustomTabPanel value={tabValue} index={1}>
                <BulkUpdateTagsTab
                  customerId={customerId}
                  control={control}
                  currentTagsToAdd={currentTagsToAdd}
                  currentTagsToRemove={currentTagsToRemove}
                />
              </CustomTabPanel>
              {isViewsEnabled && (
                <CustomTabPanel value={tabValue} index={2}>
                  <BulkUpdateViewsTab
                    customerId={customerId}
                    user={user}
                    control={control}
                    currentViewsToAdd={currentViewsToAdd}
                    currentViewsToRemove={currentViewsToRemove}
                  />
                </CustomTabPanel>
              )}
            </form>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="outlined">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={apiResponse != null || !isDirty}
            form={FORM_ID}
          >
            Confirm
          </Button>
        </DialogActions>
        <LoadingOverlay isLoading={isPending} />
      </Dialog>
    </>
  );
}
